import { Injectable } from "@angular/core";
import { NbAuthService, NbAuthJWTToken, NbAuthToken } from "@nebular/auth";
import { NbRoleProvider } from "@nebular/security";
import { map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { NbThemeService } from '@nebular/theme';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

interface HasuraClaims {
  'x-hasura-role'?: string;
  'x-hasura-company-holder-id'?: string;
  'x-hasura-company-id'?: string;
  'x-hasura-user-id'?: string;
  'x-hasura-name'?: string;
  'x-hasura-surname'?: string;
}

@Injectable({ providedIn: "root" })
export class RoleProvider extends NbRoleProvider {
  private currentToken: NbAuthToken | null = null;

  constructor(private authService: NbAuthService,private themeService: NbThemeService, private router: Router,) {
    super();
    this.authService.onTokenChange().subscribe((token: NbAuthToken) => {
      this.currentToken = token;
      const payload = (token as NbAuthJWTToken | null)?.getPayload?.();
      console.error('[RoleProvider] token changed', payload?.['hasura_claims']);
    });
    this.authService.getToken().subscribe((token: NbAuthToken) => {
      this.currentToken = token;
      const payload = (token as NbAuthJWTToken | null)?.getPayload?.();
      console.error('[RoleProvider] token snapshot', payload?.['hasura_claims']);
    });
  }

  getRole() {
    return this.authService.onTokenChange()
      .pipe(
        map((token) => {
          return token.isValid() ? token.getPayload()["hasura_claims"]['x-hasura-role'] : 'guest';
        }),
      );
  }

  setTheme() {
    this.themeService.changeTheme('light_blue');
  }

  getUserRole() {
    try {
      const token = this.getCurrentToken();
      return token?.getPayload()?.['hasura_claims']?.['x-hasura-role'] || 'guest';
    } catch (err) {
      this.router.navigateByUrl('/auth/login');
      return 'guest';
    }
  }

  async getUserRoleAsync(): Promise<string> {
    try {
      const token = await firstValueFrom(this.authService.getToken());
      this.currentToken = token;

      if (!token || typeof (token as any).isValid !== 'function' || !(token as any).isValid()) {
        return 'guest';
      }

      return (token as NbAuthJWTToken)
        .getPayload()?.['hasura_claims']?.['x-hasura-role'] || 'guest';
    } catch {
      return 'guest';
    }
  }

  getCompanyHolderId() {
    try {
      const token = this.getCurrentToken();
      return token?.getPayload()?.['hasura_claims']?.['x-hasura-company-holder-id'];
    } catch (err) {
      this.router.navigateByUrl('/auth/login');
      return null;
    }
  }

  getCompanyId() {
    try {
      const token = this.getCurrentToken();
      return token?.getPayload()?.['hasura_claims']?.['x-hasura-company-id'];
    } catch (err) {
      this.router.navigateByUrl('/auth/login');
      return null;
    }
  }

  getId() {
    try {
      const token = this.getCurrentToken();
      return token?.getPayload()?.['hasura_claims']?.['x-hasura-user-id'] || null;
    } catch (err) {
      this.router.navigateByUrl('/auth/login');
      return null;
    }
  }

  getUserDisplayName(): string {
    try {
      const claims = this.getCurrentToken()?.getPayload()?.['hasura_claims'] as HasuraClaims | undefined;
      return [claims?.['x-hasura-name'], claims?.['x-hasura-surname']].filter(Boolean).join(' ').trim();
    } catch {
      return '';
    }
  }

  private getCurrentToken(): NbAuthJWTToken | null {
    const token = this.currentToken as NbAuthJWTToken | null;
    if (token && typeof (token as any).isValid === 'function' && (token as any).isValid()) {
      return token;
    }
    return null;
  }
}
